/**
 * Honest trust labels (WP1.1, decision D1-3; KYB phase 2).
 *
 * Badges are data: a moderator awards one, and only while the evidence it
 * requires holds. The server sends the awarded key as `trustBadgeLevel`
 * (`none` when there is no public badge) and its wording as `trustBadge.label`.
 * The client never compares badge keys — it shows the label it was given, and
 * falls back to a generic sentence when an older payload carries only the key.
 * Never show scores, hits or PEP/AML counts for another company.
 */
import i18n from "../i18n";
import { formatPercent } from "../i18n/format";
import type { TrustBadge } from "../services/companies/generated/model";

/** Any awarded badge key, or `none`. Keys are data (KYB phase 2), not an enum. */
export type TrustBadgeLevel = string;

/** The two badge fields a company payload carries. */
export interface TrustBadgeSource {
  trustBadgeLevel?: TrustBadgeLevel | null;
  trustBadge?: Partial<TrustBadge> | null;
}

export const awardedBadgeLabel = (): string => i18n.t("domain:trust.awarded");
export const notReviewedLabel = (): string => i18n.t("domain:trust.notReviewed");
export const notAssessedLabel = (): string => i18n.t("domain:trust.notAssessed");

/** True when the company holds a public badge (any key). */
export function hasTrustBadge(source: TrustBadgeSource | null | undefined): boolean {
  const level = source?.trustBadgeLevel;
  return !!level && level !== "none";
}

/** The badge wording from the server, or a generic sentence. */
export function trustBadgeLabel(source: TrustBadgeSource | null | undefined): string {
  if (!hasTrustBadge(source)) return notReviewedLabel();
  return source?.trustBadge?.label || awardedBadgeLabel();
}

/** The badge description from the server (empty when there is none). */
export function trustBadgeDescription(source: TrustBadgeSource | null | undefined): string {
  return hasTrustBadge(source) ? (source?.trustBadge?.description ?? "") : "";
}

/** CSS modifier suffix shared by every badge style: `--reviewed` or `--none`. */
export function trustBadgeVariant(source: TrustBadgeSource | null | undefined): "reviewed" | "none" {
  return hasTrustBadge(source) ? "reviewed" : "none";
}

/** A score is "not assessed" when the backend sends null/undefined. A real 0 is a score. */
export function isAssessed(score: number | null | undefined): score is number {
  return typeof score === "number";
}

/** "42%" for an assessed score (0 included), "Not assessed" otherwise. */
export function formatScore(score: number | null | undefined): string {
  return isAssessed(score) ? formatPercent(score) : notAssessedLabel();
}

/** Clamp an assessed score to 0–100 for progress bars; unassessed → 0 width. */
export function scoreBarWidth(score: number | null | undefined): string {
  return `${isAssessed(score) ? Math.min(100, Math.max(0, score)) : 0}%`;
}
